import EF from 'ef/ef';
import utils from 'lib/utils';
import CourseSettingsService from 'services/course-settings-service';
import LocaleService from 'services/locale-service';
import VideoServiceBase from 'services/video-service-base';

export default class VideoServiceDash extends VideoServiceBase {
  constructor(view) {
    super(view);
    var dashControlbar = require('views/block/video/templates/dash-controlbar');
    var videoEl = view.el.querySelector(`.accessibility-wrapper video`);
    videoEl.insertAdjacentHTML('afterend', dashControlbar({
      transcript: view.model.get('transcript')
    }));
    if(view.model.has('height') && view.el && videoEl){
      let barHeight = view.el.querySelector('#videoController').offsetHeight;
      videoEl.height = view.model.get('height') - barHeight;
    }
    if (view.model.has('width') && videoEl){
      videoEl.width = view.model.get('width');
    }
    Chaplin.mediator.subscribe('media:captions:change', this.toggleCaptions.bind(this));
  }

  buildVideoPlayer(metadata, options) {
    const info = super.buildVideoPlayer(metadata, options);

    this.player = dashjs.MediaPlayer().create();
    this.player.getDebug().setLogToBrowserConsole(false);

    // autoplay is false since the view handles that behavior
    this.player.initialize(this.video, info.manifestUrl, false);

    // Cross browser support for the native control bar is poor
    // The native controls for Firefox did not display the closed captioning button
    // As such dash.js provides this js/html/css controlbar
    this.controlbar = new ControlBar(this.player);
    this.controlbar.initialize();

    // By default captions are displayed on first load.
    // There is no setting in dash.js to default to not showing captions
    // This will set dash.js to not display captions and have the ControlBar styles match
    this.player.on(dashjs.MediaPlayer.events.TEXT_TRACKS_ADDED, () => {
      setTimeout(() => this.player.setTextTrack(-1), 0);
      let nextSibling = this.video.nextSibling;
      if(nextSibling !== null) {
        let elements = nextSibling.querySelectorAll('#captionMenu li');
        if(elements.length > 0) {
          Array.from(elements).forEach(item => {
            if (item.innerText === 'OFF') {
              item.classList.remove('menu-item-unselected');
              item.classList.add('menu-item-selected');
            } else {
              item.classList.remove('menu-item-selected');
              item.classList.add('menu-item-unselected');
            }
          });
        }
      }
    });

    // Accessibility concerns for the control bar
    // * The menu must be next to the button which opens them,
    //   so the tab order is correct
    // * Each menu item must have a button with tabindex set to 0
    //   so that it can be tabbed into
    // * Button elements forward the enter key to a click event
    //   so they work with a keyboard while spans/divs do not
    this.player.on(dashjs.MediaPlayer.events.CAN_PLAY, () => {
      this.moveElement('#captionMenu').toAfter('#captionBtn');
      this.wrapMenuItemsWithButton('#captionMenu');

      this.moveElement('#bitrateMenu').toAfter('#bitrateListBtn');
      this.wrapMenuItemsWithButton('#bitrateMenu');
    });

    this.addAriaLabel('#playPauseBtn, #playPauseBtn button', 'videoAccessibility.playpause');
    this.addAriaLabel('#videoTime', 'videoAccessibility.time');
    this.addAriaLabel('.seekContainer, #seekbar', 'videoAccessibility.seeker');
    this.addAriaLabel('#videoDuration', 'videoAccessibility.duration');
    this.addAriaLabel('#captionBtn, #captionBtn button', 'videoAccessibility.captions');
    this.addAriaLabel('#muteBtn, #muteBtn button', 'videoAccessibility.mute');
    this.addAriaLabel('#volumebar', 'videoAccessibility.volumebar');
    this.addAriaLabel('#trackSwitchBtn', 'videoAccessibility.tracks');
    this.addAriaLabel('#bitrateListBtn, #bitrateListBtn button', 'videoAccessibility.bitrate');
    this.addAriaLabel('#fullscreenBtn, #fullscreenBtn button', 'videoAccessibility.fullscreen');
    this.addAriaLabel('#transcriptBtn, #transcriptBtn button', 'videoAccessibility.transcript');

    this.initializeMuteState();
    return true;
  }

  addAriaLabel(selector, key) {
    const translation = LocaleService.t(key);
    if(translation !== '' && translation !== key) {
      let nextSibling = this.video.nextSibling;
      if(nextSibling !== null) {
        let elements = nextSibling.querySelectorAll(selector);
        if(elements.length > 0) {
          Array.from(elements).forEach((element) => {
            element.setAttribute('aria-label', translation);
          });
        }
      }
    }
  }

  wrapMenuItemsWithButton(selector) {
    let nextSibling = this.video.nextSibling;
    if(nextSibling !== null) {
      let elements = nextSibling.querySelectorAll(`${selector} li`);
      if(elements.length > 0) {
        Array.from(elements).forEach(item => {
          var button = document.createElement('button');
          button.setAttribute('tabindex', 0);
          button.innerText = item.innerText;
          button.setAttribute('aria-label', item.innerText);
          item.innerText = '';
          item.appendChild(button);
        });
      }
    }
  }

  moveElement(sourceSelector) {
    var nextSibling = this.video.nextSibling;
    if(nextSibling !== null) {
      var sourceElement = this.video.nextSibling.querySelector(sourceSelector);
    }
    return {
      toAfter: (targetSelector) => {
        if(sourceElement && this.video.nextSibling) {
          this.video.nextSibling.insertBefore(
            sourceElement,
            this.video.nextSibling.querySelector(targetSelector).nextSibling
          );
        }
      }
    };
  }

  setupEvents(callbacks) {
    super.setupEvents();
    this.player.on(dashjs.MediaPlayer.events.CAN_PLAY, this.onReady.bind(this));
    this.player.on(dashjs.MediaPlayer.events.PLAYBACK_STARTED, callbacks.play);
    this.player.on(dashjs.MediaPlayer.events.PLAYBACK_PAUSED, callbacks.pause);
    this.player.on(dashjs.MediaPlayer.events.PLAYBACK_PAUSED, this.pause.bind(this));
    this.player.on(dashjs.MediaPlayer.events.PLAYBACK_TIME_UPDATED, this.onTime.bind(this));
    this.player.on(dashjs.MediaPlayer.events.PLAYBACK_ENDED, callbacks.end);
    this.player.on(dashjs.MediaPlayer.events.PLAYBACK_SEEKING, this.onSeek.bind(this));
    this.player.on(dashjs.MediaPlayer.events.ERROR, this.error.bind(this));

    this.destroyEvents = () => {
      this.player.off(dashjs.MediaPlayer.events.CAN_PLAY, this.onReady);
      this.player.off(dashjs.MediaPlayer.events.PLAYBACK_STARTED, callbacks.play);
      this.player.off(dashjs.MediaPlayer.events.PLAYBACK_PAUSED, callbacks.pause);
      this.player.off(dashjs.MediaPlayer.events.PLAYBACK_PAUSED, this.pause);
      this.player.off(dashjs.MediaPlayer.events.PLAYBACK_TIME_UPDATED, this.onTime);
      this.player.off(dashjs.MediaPlayer.events.PLAYBACK_ENDED, callbacks.end);
      this.player.off(dashjs.MediaPlayer.events.PLAYBACK_SEEKING, this.onSeek);
      this.player.off(dashjs.MediaPlayer.events.ERROR, this.error);
    };
  }

  error(e) {
    console.error('Error playing video:', e);
  }

  toggleCaptions() {
    // Cycle through the captions
    var index = this.player.getCurrentTextTrackIndex();
    var length = this.video.textTracks.length;
    if (index + 1 >= length) {
      this.player.setTextTrack(-1);
    } else {
      this.player.setTextTrack(index + 1);
    }
  }

  onSeek(e) {
    if(!EF.options.local && this.progress) {
      if(e.seekTime > this.progress) {
        this.seek(this.currentTime);
      }
    }
  }

  onTime(e) {
    var seekbar = document.getElementById('seekbar');
    if(seekbar !== null) {
      seekbar.value = e.time;
      if(e.time > this.progress) {
        this.progress = e.time;
      }
      this.currentTime = e.time;
    }
  }

  stop() {
    this.addAriaLabel('#playPauseBtn button', 'videoAccessibility.play');
    if (this.player) {
      this.player.pause();
      this.player.seek(0);
    }
  }

  play() {
    this.addAriaLabel('#playPauseBtn button', 'videoAccessibility.pause');
    if(this.player) {
      this.player.play();
      setTimeout(() => {
        if(this.player && this.player.isPaused()) {
          let startContainer = this.video.nextSibling.querySelector('#startContainer');
          let label = startContainer.querySelector('label[for="startButton"]');
          if(label !== null) {
            label.innerText = LocaleService.t('videoAccessibility.start');
          }
          if(startContainer !== null) {
            startContainer.classList.remove('hidden');
            startContainer.style.top = ((this.video.offsetHeight - startContainer.clientHeight) / 2) + "px";
            startContainer.style.left = ((this.video.offsetWidth - startContainer.clientWidth) / 2) + "px";
            startContainer.addEventListener('click', () => {
              let container = this.video.nextSibling.querySelector('#startContainer');
              if(container !== null) {
                container.classList.add('hidden');
              }
              this.player.play();
            }, true);
          }
        }
      }, 0);
    }
  }

  pause() {
    this.addAriaLabel('#playPauseBtn button', 'videoAccessibility.play');
    if (this.player) {
      this.player.pause();
    }
  }

  mute() {
    this.addAriaLabel('#muteBtn button', 'videoAccessibility.unmute');
    if (this.player) {
      this.lastVolume = this.player.getVolume();
      this.player.setMute(true);
      this.controlbar.setVolume(0);
    }
  }

  unmute() {
    this.addAriaLabel('#muteBtn button', 'videoAccessibility.mute');
    if (this.player) {
      this.player.setMute(false);
      this.controlbar.setVolume(this.lastVolume || 1);
    }
  }

  getState() {
    if (!this.player) {
      return 'STOPPED';
    } else if (this.player.isPaused()) {
      return 'PAUSED';
    } else {
      return 'PLAYING';
    }
  }

  getPosition() {
    return this.player ? this.player.time() : 0;
  }

  getDuration() {
    return this.player ? this.player.duration() : 0;
  }

  seek(position) {
    if( position < 0 || position > this.getDuration()) return;
    if (this.player) {
      this.player.seek(position);
    }
  }

  getMute() {
    return this.player ? this.player.isMuted() : false;
  }

  setMute(mute) {
    if (mute) {
      this.lastVolume = this.player.getVolume();
      this.controlbar.setVolume(0);
    } else {
      this.controlbar.setVolume(this.lastVolume || 1);
    }
    this.player.setMute(mute);
  }

  increaseVolume(amount) {
    const currVolume = this.player.getVolume();
    if (this.controlbar && amount >= 0 && amount <= 1) {
      this.controlbar.setVolume(parseFloat(currVolume) + parseInt(amount) / 10);
    }
  }

  decreaseVolume(amount) {
    const currVolume = this.player.getVolume();
    if (this.controlbar && amount >= 0 && amount <= 1) {
      this.controlbar.setVolume(parseFloat(currVolume) - (parseInt(amount) / 10));
    }
  }

  dispose() {
    if(this.destroyEvents) {
      this.destroyEvents();
    }
    super.dispose();
    // This does not work currently
    // There's a pending PR to fix this https://github.com/Dash-Industry-Forum/dash.js/pull/2107
    //this.controlbar.destroy();
  }
}
